import { ThemeProvider, resolveStage } from "@nu-assist/shared-fe";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { Toaster } from "sonner";
import { CompareGamificationProvider } from "./features/CompareGamificationProvider";
import { PreviewProvider } from "./features/PreviewProvider";
import { SelectedImportProvider } from "./features/imports/selection/SelectedImportContext";
import { MediaSessionProvider } from "./features/media/MediaSessionProvider";
import { AppRoutes } from "./routing/routes";
import { installUserActivityHeader } from "./shared/userActivity";
import { applyStageFavicon } from "./shell/setStageFavicon";
// Inter's `@font-face` rules. Imported HERE and not from `index.css` because
// the report export inlines `index.css` into a file that is opened offline,
// where a font `url()` is dead. See `@nu-assist/ui/src/fonts.css`.
import "@nu-assist/ui/fonts.css";
import "./index.css";

// Swap the static `<link rel="icon">` (set in `index.html` to the prod
// mark) to the per-stage favicon BEFORE render so the tab affordance
// is correct from first paint. On prod this resolves to the same href
// already in the HTML — the call is a no-op visually. See
// `src/shell/setStageFavicon.ts` for the green/orange/neutral mapping.
applyStageFavicon(resolveStage(), import.meta.env.VITE_PERSONAL_STAGE);

// Personal environments only: mark requests a person caused with
// `X-Nu-User-Active` so the nightly reaper can tell use from an idle tab's
// polls. A no-op on dev and prod builds (no VITE_PERSONAL_STAGE). Before render,
// so the first request already goes through it. See `src/shared/userActivity.ts`.
installUserActivityHeader({
  personalStage: import.meta.env.VITE_PERSONAL_STAGE,
  apiUrl: import.meta.env.VITE_API_URL,
  target: window,
});

const root = document.getElementById("root");
if (!root) throw new Error("#root not found");

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function render() {
  // root is guaranteed non-null by the guard above.
  createRoot(root as HTMLElement).render(
    <StrictMode>
      <ThemeProvider>
        <QueryClientProvider client={queryClient}>
          {/* Whether logos and avatars load from `/media` on CloudFront; off
              (presign) until it answers yes. */}
          <MediaSessionProvider>
            <BrowserRouter>
              <PreviewProvider>
                {/* `?gamification=off` — a SEPARATE, independent flag,
                  not a rung on the preview ladder. It gates one decoration
                  inside the Compare modal (Trello #790, reviewed and shipped on
                  before it ships on); `?preview=N` gates whole pages. Nested
                  rather than merged so neither can re-key the other — see the
                  provider's docstring. */}
                <CompareGamificationProvider>
                  {/* The app-wide "current import" shared by AI Review and
                    Backchannel Tree. Inside BrowserRouter because the
                    selection bar resolves `?import=` / `?importId=` deep
                    links via useSearchParams. */}
                  <SelectedImportProvider>
                    <AppRoutes />
                  </SelectedImportProvider>
                </CompareGamificationProvider>
              </PreviewProvider>
            </BrowserRouter>
          </MediaSessionProvider>
          {/* App-wide toast portal (sonner) — used by the slide creator + future features. */}
          <Toaster richColors position="top-right" />
        </QueryClientProvider>
      </ThemeProvider>
    </StrictMode>
  );
}

// Opt-in MSW dev mocking (VITE_USE_MSW). The mocks graph is DYNAMICALLY imported
// behind a `DEV && flag` gate so it code-splits OUT of production: in any
// `vite build`, `import.meta.env.DEV` is a static `false`, so Rollup dead-code-
// eliminates this whole branch — the `msw` devDependency, the handler graph, and
// the fabricated `/auth/me` auth handler never enter the prod bundle. The `DEV`
// half is the load-bearing guard (defence-in-depth): even a misconfigured prod
// build with `VITE_USE_MSW=1` cannot start the auth-bypass worker. Awaiting
// before render ensures the worker intercepts before the first request fires.
async function startMocksIfEnabled(): Promise<void> {
  if (!(import.meta.env.DEV && import.meta.env.VITE_USE_MSW)) return;
  const { startMswWorker } = await import("./mocks/browser");
  await startMswWorker();
}

void startMocksIfEnabled().then(render);
